import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, Zoom, Mousewheel } from 'swiper/modules';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faBuilding, faLocationDot, faCalendarDays, faMoneyBill } from '@fortawesome/free-solid-svg-icons';
import LoadingImage from '../Common/LoadingImage';
import ImageModal from '../Common/ImageModal';
import CountryDisplay from '../Common/CountryDisplay';

const ProjectDetailDesktop = ({ project, language, siteInfo }) => {
  const { t } = useTranslation();
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const gallery_images = project?.gallery_images || [];
  const isRTL = language == 'ar' ? true : false;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-[80vh]">
        <LoadingImage
          src={project.hero_image}
          alt={project.title}
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70">
          <div className="container flex flex-col justify-end px-8 pb-24 mx-auto h-full">
            <div className="flex gap-6 items-center mb-6">
              <span className={`px-4 py-1.5 rounded-full text-base font-semibold ${project.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'
                } text-white`}>
                {t(`projects.${project.status}`)}
              </span>
              <span className="text-xl text-white">{project.location}</span>
            </div>
            <h1 className="mb-6 text-6xl font-bold text-white">{project.title}</h1>
          </div>
        </div>
      </section>

      <div className="container px-8 py-20 mx-auto">
        {/* Project Details */}
        <div className="p-12 mb-16 bg-white rounded-xl shadow-sm">
          <h2 className="mb-8 text-3xl font-bold">{t('projects.overview')}</h2>
          <div className="flex gap-16">
            {project.description && (
              <div className="flex-1">
                <p className="text-xl text-gray-600 leading-relaxed text-justify">{project.description}</p>
              </div>
            )}

            <div className="w-96 space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100">
                  <FontAwesomeIcon icon={faBuilding} size="lg" className="text-stone-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.type')}</h3>
                  <p className="text-xl font-semibold text-gray-900">{project.type}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100">
                  <FontAwesomeIcon icon={faLocationDot} size="lg" className="text-stone-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.location')}</h3>
                  <CountryDisplay
                    countryCode={project.country_code}
                    city={project.city}
                  />
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100">
                  <FontAwesomeIcon icon={faCalendarDays} size="lg" className="text-stone-600" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.year')}</h3>
                  <p className="text-xl font-semibold text-gray-900">{project.year}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-100">
                  <FontAwesomeIcon icon={faMoneyBill} size="lg" className="text-stone-600" />
                </div>
                {project.cost > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">{t('projects.cost')}</h3>
                    <p className="text-xl font-semibold text-gray-900">
                      {Number(project.cost).toLocaleString()} {project.currency}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Gallery Section */}
        {gallery_images.length > 0 && (
          <div className="mb-20">
            <h2 className="mb-12 text-4xl font-semibold">{t('projects.gallery')}</h2>
            <Swiper
              modules={[Navigation, Pagination, Autoplay, Zoom, Mousewheel]}
              dir={isRTL ? 'rtl' : 'ltr'}
              spaceBetween={24}
              slidesPerView={3}
              navigation={{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }}
              pagination={{
                clickable: true,
                el: '.swiper-pagination'
              }}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}

              keyboard={true}
              mousewheel={{
                enabled: true,
                forceToAxis: true,
                releaseOnEdges: true,
                sensitivity: 2,
              }}
              loop={true}
              className="gallery-swiper"
            >
              {gallery_images.map((image, index) => (
                <SwiperSlide key={index}>
                  <div
                    className="aspect-[4/3] cursor-pointer rounded-xl overflow-hidden"
                    onClick={() => {
                      setCurrentImage(index);
                      setShowGalleryModal(true);
                    }}
                  >
                    <LoadingImage
                      src={image}
                      alt=""
                      className="object-cover w-full h-full transition-transform duration-300 hover:scale-110"
                    />
                  </div>
                </SwiperSlide>
              ))}
              <div className="swiper-button-prev !text-white"></div>
              <div className="swiper-button-next !text-white"></div>
              <div className="swiper-pagination !bottom-4"></div>
            </Swiper>
          </div>
        )}

        {/* Contact Button */}
        <div className="text-center">
          <Link
            to="/contact"
            className="inline-flex items-center px-12 py-5 text-xl font-semibold text-white rounded-lg shadow-xl transition-all bg-stone-800 hover:bg-white hover:text-stone-900 hover:transform hover:-translate-y-1"
          >
            {t('footer.contact')}
            <FontAwesomeIcon icon={faArrowRight} className="ml-3" />
          </Link>
        </div>
      </div>

      {/* Modal */}
      {showGalleryModal && (
        <ImageModal
          images={gallery_images}
          onClose={() => setShowGalleryModal(false)}
          initialSlide={currentImage}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default ProjectDetailDesktop;
